import React, { useContext, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import { AuthContext } from '../reducers/Auth/AuthContext';
import Button from '@mui/material/Button';
import { EditField } from './EditField';
import { EditYesNo } from './EditYesNo';
import BarLoader from 'react-spinners/BarLoader';
import { uuid } from '../utils/uuid';
import { PlaceStoreContext } from '../reducers/Place/PlaceContext';
import { updateToppingGroup } from '../apis/updateToppingGroup';
import { addNewToppingGroup } from '../apis/addNewToppingGroup';
import { deleteToppingGroup } from '../apis/deleteToppingGroup';
import AreYouSureYesNo from '../components/AreYouSureYesNo';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { COLORS } from '../utils/colors';
import PublishButton from './PublishButton';

const ManageToppingGroup = (props) => {
  const { toppingGroupId, onClose } = props;
  const [placeState] = useContext(PlaceStoreContext);
  const [authState] = useContext(AuthContext);
  const [initialData, setInitialData] = useState(null);
  const [toppingGroupDelete, setToppingGroupDelete] = useState({
    visible: false,
    isDeleting: false,
  });

  const {
    toppings: { toppingGroupId__toppingsGroups },
  } = placeState;
  const {
    user: { placeId },
  } = authState;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let initialValues = null;
    if (toppingGroupId === 'new') {
      initialValues = {
        id: uuid(),
        name: '',
        active: true,
      };
    } else if (toppingGroupId) {
      const toppingGroup = toppingGroupId__toppingsGroups[toppingGroupId];
      initialValues = {
        id: toppingGroup.id,
        name: toppingGroup.name,
        active: toppingGroup.active,
      };
    }
    initialValues && setInitialData({ initialValues });
    if (toppingGroupDelete.visible) {
      setToppingGroupDelete({ visible: false, isDeleting: false });
      // history.replace(`/manage/toppings`);
      onClose();
    }
  }, [toppingGroupId]);

  const handleToppingGroupDelete = () => {
    setToppingGroupDelete({ visible: true, isDeleting: false });
  };

  const toppingGroupOnNo = () => {
    setToppingGroupDelete({ visible: false, isDeleting: false });
  };

  const toppingGroupOnYes = () => {
    setToppingGroupDelete({ visible: true, isDeleting: true });
    deleteToppingGroup(placeId, toppingGroupId);
  };

  if (initialData === null || !toppingGroupId) return null;

  if (toppingGroupDelete.isDeleting) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: `calc(100vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
          height: '80vh',
          margin: '0 auto',
        }}
      >
        <Grid container spacing={2} style={{ padding: '16px' }}>
          <Grid item xs={12}>
            <AreYouSureYesNo
              onNo={toppingGroupOnNo}
              onYes={toppingGroupOnYes}
              loading={toppingGroupDelete.isDeleting}
              loadingMessage="Deleting topping ..."
              warning="This topping will be deleted!"
            />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Grid container style={{ marginTop: '40px' }}>
      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={initialData.initialValues}
          validationSchema={Yup.object({
            active: Yup.boolean().required('Required'),
            name: Yup.string()
              .max(64, 'Must be 64 characters or less')
              .test('value', 'Must not be empty', (value) => {
                return value !== '';
              })
              .test(
                'value',
                'Must have at least one character',
                (value) => value && value.trim().length >= 1,
              )
              .required('Required'),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            if (!placeId) return;
            setSubmitting(true);

            if (toppingGroupId === 'new') {
              await addNewToppingGroup(placeId, values);
            } else {
              await updateToppingGroup(placeId, toppingGroupId, values);
            }

            setSubmitting(false);
            onClose();
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => {
            if (isSubmitting)
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: `calc(90vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
                    height: '80vh',
                    margin: '0 auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ margin: 0, paddingBottom: '20px', fontWeight: 'bold' }}>
                      SAVING CHANGES
                    </p>
                    <BarLoader height={4} width={100} color="#000000" loading={true} />
                  </div>
                </div>
              );
            const submitDisabled = isSubmitting || _.isEqual(initialData.initialValues, values);
            const title =
              toppingGroupId === 'new'
                ? `NEW TOPPING GROUP`
                : toppingGroupId__toppingsGroups[toppingGroupId].name.toUpperCase();
            return (
              <div>
                <Form encType="multipart/form-data">
                  <Grid container spacing={2} justify="center" style={{ maxWidth: '500px' }}>
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                          fontWeight: 'bold',
                          color: COLORS.INFO,
                        }}
                      >
                        {title}
                      </p>
                      <PublishButton name="PUBLISH" disabled={submitDisabled} />
                    </Grid>
                    <Grid item xs={12}>
                      <EditYesNo label="Active" name="active" />
                    </Grid>

                    <Grid item xs={12}>
                      <EditField label="Name (64 max characters)" name="name" />
                    </Grid>
                    {toppingGroupId !== 'new' && (
                      <Grid item xs={12} style={{ marginTop: 16 }}>
                        {!toppingGroupDelete.visible ? (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={handleToppingGroupDelete}
                          >
                            <span>DELETE</span>
                            <DeleteForeverIcon className="s-24-i txt-white ml-16" />
                          </Button>
                        ) : (
                          <div className="ml-16">
                            <AreYouSureYesNo
                              onNo={toppingGroupOnNo}
                              onYes={toppingGroupOnYes}
                              loading={toppingGroupDelete.isDeleting}
                              loadingMessage="Deleting topping group ..."
                              warning="This topping group will be deleted and all the toppings in it!"
                            />
                          </div>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Form>
                <div style={{ height: '200px' }} />
              </div>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default ManageToppingGroup;
